import { useEffect, useMemo, useState } from "react";

import { orgSettingsApi } from "@/api/agencies";
import { Alert } from "@/components/Alert";
import { Field } from "@/components/Field";
import { FormSelect } from "@/components/FormSelect";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Toast } from "@/components/Toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { usePermissions } from "@/hooks/usePermissions";

const COMMON_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "America/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Kolkata",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Australia/Sydney",
  "Pacific/Auckland",
];

const FORM_KEYS = ["name", "timezone", "contact_email", "contact_phone"];

export function OrgSettingsPage() {
  const { can } = usePermissions();
  const canEdit = can("organization.settings.edit");

  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [toastMessage, setToastMessage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await orgSettingsApi.get();
        setSettings(data);
        setForm(data);
      } catch (err) {
        setError(err.message || "Failed to load organization settings.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isDirty = useMemo(() => {
    if (!form || !settings) return false;
    return FORM_KEYS.some((key) => (form[key] ?? "") !== (settings[key] ?? ""));
  }, [form, settings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setFieldErrors((fe) => ({ ...fe, [name]: null }));
  };

  const discard = () => {
    setForm(settings);
    setFieldErrors({});
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isDirty || saving) return;
    setSaving(true);
    setFieldErrors({});
    setError(null);
    try {
      const updated = await orgSettingsApi.update(form);
      setSettings(updated);
      setForm(updated);
      setToastMessage("Organization settings saved.");
    } catch (err) {
      if (err.errors) {
        setFieldErrors(err.errors);
      } else {
        setError(err.message || "Failed to save settings.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageSkeleton />;
  }

  const timezoneOptions = [
    ...COMMON_TIMEZONES.map((tz) => ({ value: tz, label: tz })),
    ...(form?.timezone && !COMMON_TIMEZONES.includes(form.timezone)
      ? [{ value: form.timezone, label: form.timezone }]
      : []),
  ];

  return (
    <PageShell>
      <Toast message={toastMessage} onClose={() => setToastMessage(null)} />

      <PageHeader
        title="Organization Settings"
        description="Edit your agency's profile, timezone, and contact information."
      />

      <PageBody>
        {error ? <Alert type="error">{error}</Alert> : null}

        {form ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">General</CardTitle>
                <CardDescription>
                  The organization name shown to your team across the product.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Field label="Organization name" name="name" error={fieldErrors.name?.[0]}>
                  {(props) => (
                    <Input
                      {...props}
                      value={form.name ?? ""}
                      required
                      disabled={!canEdit}
                      onChange={handleChange}
                    />
                  )}
                </Field>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Regional</CardTitle>
                <CardDescription>
                  Timezone drives scheduling and what “today” means for this agency.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Field label="Timezone" name="timezone" error={fieldErrors.timezone?.[0]}>
                  {(props) => (
                    <FormSelect
                      id={props.id}
                      name={props.name}
                      value={form.timezone ?? "UTC"}
                      disabled={!canEdit}
                      onValueChange={(v) =>
                        handleChange({ target: { name: props.name, value: v } })
                      }
                      options={timezoneOptions}
                      placeholder="Select timezone…"
                    />
                  )}
                </Field>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Contact</CardTitle>
                <CardDescription>
                  Optional public contact details for your organization.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field
                  label="Contact email"
                  name="contact_email"
                  error={fieldErrors.contact_email?.[0]}
                >
                  {(props) => (
                    <Input
                      {...props}
                      type="email"
                      value={form.contact_email ?? ""}
                      disabled={!canEdit}
                      onChange={handleChange}
                    />
                  )}
                </Field>
                <Field
                  label="Contact phone"
                  name="contact_phone"
                  error={fieldErrors.contact_phone?.[0]}
                >
                  {(props) => (
                    <Input
                      {...props}
                      type="tel"
                      value={form.contact_phone ?? ""}
                      disabled={!canEdit}
                      onChange={handleChange}
                    />
                  )}
                </Field>
              </CardContent>
              {canEdit ? (
                <CardFooter className="gap-2 border-t border-border px-4 pt-4">
                  <Button type="submit" disabled={!isDirty || saving}>
                    {saving ? "Saving…" : "Save changes"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={!isDirty || saving}
                    onClick={discard}
                  >
                    Discard
                  </Button>
                </CardFooter>
              ) : null}
            </Card>
          </form>
        ) : null}
      </PageBody>
    </PageShell>
  );
}
